import { useCallback, useEffect, useState } from 'react';
import { useSnackbarProvider } from 'src/hooks';
import api from '../api';
import { IAvailableProperty } from '../ts/interfaces/Owner';

interface UseAvailablePropertiesParams {
  ownerId: string;
  autoLoad?: boolean; // Whether to automatically load data on mount
}

interface UseAvailablePropertiesReturn {
  // State
  properties: IAvailableProperty[];
  isLoading: boolean;
  error: string | null;
  count: number;
  page: number;
  rowsPerPage: number;
  searchQuery: string;

  // Actions
  onGetAll: (page?: number, rowsPerPage?: number) => Promise<void>;
  onSearch: (query: string) => void;
  onRefresh: () => Promise<void>;
  onReset: () => void;

  // Computed properties
  isToolbarDisabled: boolean;
  hasData: boolean;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const useAvailableProperties = (
  params: UseAvailablePropertiesParams
): UseAvailablePropertiesReturn => {
  const { ownerId, autoLoad = true } = params;

  // State management
  const [properties, setProperties] = useState<IAvailableProperty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  // Computed properties
  const isToolbarDisabled = isLoading;
  const hasData = properties.length > 0;
  const totalPages = Math.ceil(count / rowsPerPage);
  const hasNextPage = page < totalPages - 1;
  const hasPreviousPage = page > 0;

  const onGetAll = useCallback(
    async (pageNumber = 0, perPage: number = rowsPerPage) => {
      if (!ownerId) {
        console.warn('Cannot fetch available properties: ownerId is required');
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const params = {
          page: pageNumber,
          rowsPerPage: perPage,
          ...(searchQuery && { q: searchQuery })
        };
        if (searchQuery) {
          setPage(0);
        } else {
          setPage(pageNumber);
        }

        const response = await api.getAvailableProperties(ownerId, params);
        setProperties(response.data || []);
        setCount(response.count || 0);
        setRowsPerPage(perPage);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch available properties';
        setError(errorMessage);
        setProperties([]);
        setCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [searchQuery, rowsPerPage, ownerId]
  );

  useEffect(() => {
    if (error) {
      showSnackbar(VariantType.error, error, SnackTypes.none);
      setError(null);
    }
  }, [error, showSnackbar, SnackTypes, VariantType]);

  useEffect(() => {
    if (autoLoad) {
      onGetAll();
    }
  }, [onGetAll, autoLoad, searchQuery]);

  const onSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const onRefresh = useCallback(async () => {
    await onGetAll(page, rowsPerPage);
  }, [onGetAll, page, rowsPerPage]);

  const onReset = useCallback(() => {
    setProperties([]);
    setError(null);
    setCount(0);
    setPage(0);
    setRowsPerPage(20);
    setSearchQuery('');
  }, []);

  return {
    // State
    properties,
    isLoading,
    error,
    count,
    page,
    rowsPerPage,
    searchQuery,

    // Actions
    onGetAll,
    onSearch,
    onRefresh,
    onReset,

    // Computed properties
    isToolbarDisabled,
    hasData,
    totalPages,
    hasNextPage,
    hasPreviousPage
  };
};

export default useAvailableProperties;
